/**
 * Header / Footer drop-zone handlers for the report-mode builder.
 * Extracted from ReportBuilderPage to keep page render slimmer.
 *
 * Returns the three event handlers required by a header or footer DOM node:
 *   { onDragOver, onDragLeave, onDrop }
 *
 * Required context:
 *   - zone: 'header' | 'footer'
 *   - getMaxZIndex(): returns the next free z-index integer
 *   - setVisuals(updater): React state setter
 *   - setActiveVisual(id): React state setter
 *   - reportHeader / reportFooter: zone configs (only `height` is read here)
 */
import { genId } from './genId';


const ZONE_HIGHLIGHT_CLASSES = ['border-sky-400/60', 'bg-sky-500/10'];
const SUPPORTED_DRAG_TYPES = ['image', 'rich_text', 'shape'];

function defaultHeaderViz(vizType, headerHeight) {
  const base = {
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    freePosition: { x: 10, y: 10, w: vizType === 'image' ? 120 : 200, h: Math.min(40, (headerHeight || 60) - 10) },
    _zone: 'header',
  };
  if (vizType === 'image') return { ...base, title: 'Header Image', imageUrl: '', imageFit: 'contain' };
  if (vizType === 'rich_text') return { ...base, title: 'Header Text', textContent: '{{report_name}}', textSize: 14, textBold: true };
  // shape
  return { ...base, title: 'Header Shape', shapeType: 'line_h', shapeFill: '#3b82f6', shapeBorder: '#3b82f6', shapeOpacity: 1 };
}

function defaultFooterViz(vizType, footerHeight) {
  const base = {
    rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
    freePosition: { x: 10, y: 5, w: 200, h: Math.min(24, (footerHeight || 40) - 10) },
    _zone: 'footer',
  };
  if (vizType === 'image') return { ...base, title: 'Footer Image', imageUrl: '', imageFit: 'contain' };
  if (vizType === 'rich_text') return { ...base, title: 'Footer Text', textContent: 'Page {{page}} of {{total_pages}} | {{date}}', textSize: 9 };
  // shape
  return { ...base, title: 'Footer Shape', shapeType: 'line_h', shapeFill: '#666', shapeBorder: '#666', shapeOpacity: 1 };
}

export function makeZoneDropHandlers({ zone, getMaxZIndex, setVisuals, setActiveVisual, reportHeader, reportFooter }) {
  const onDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add(...ZONE_HIGHLIGHT_CLASSES);
  };
  const onDragLeave = (e) => {
    e.currentTarget.classList.remove(...ZONE_HIGHLIGHT_CLASSES);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove(...ZONE_HIGHLIGHT_CLASSES);
    const vizType = e.dataTransfer.getData('text/plain');
    if (!SUPPORTED_DRAG_TYPES.includes(vizType)) return;
    const idPrefix = vizType.slice(0, 3) + (zone === 'header' ? '_hdr_' : '_ftr_');
    const id = genId(idPrefix);
    const z = getMaxZIndex() + 1;
    const builder = zone === 'header' ? defaultHeaderViz : defaultFooterViz;
    const sizeRef = zone === 'header' ? reportHeader : reportFooter;
    const newViz = { id, vizType, zIndex: z, ...builder(vizType, sizeRef?.height) };
    setVisuals(prev => [...prev, newViz]);
    setActiveVisual(id);
  };
  return { onDragOver, onDragLeave, onDrop };
}
